/**
 * astroVastuConfirm — the Astro-Vastu CONFIRM write path (owner ruling
 * 2026-07-03: "the charts are generated from birth details, I have nothing to
 * override — I want a tick box; Confirm astro inputs should BE that tick").
 *
 * Writes ONE field on the project doc — `astro_vastu_confirmation` — via the
 * same client-side `isTeam()` project-update rule every other project-field
 * writer uses (see projectStore.setProjectLinkedGroup: an ordinary project
 * field, NOT gate state, so the `gateStateUnchanged()` guard is untouched).
 * No callable, no firestore.rules change.
 *
 * ── THE CONTRACT (fixed — Forge's composer read-side mirrors this exact shape) ──
 *   astro_vastu_confirmation: {
 *     confirmed_by: string,        // staff uid (Firebase Auth uid)
 *     confirmed_by_name: string,
 *     confirmed_at: string,        // ISO
 *     snapshot: {
 *       breadwinner: { name, ascendant, ideal_zone },
 *       members: [{ name, relationship, ascendant, ideal_zone,
 *                   positive_alternatives: string[],
 *                   matched_bedroom: string|null }],
 *       allocation: [{ room, member, source }],
 *       entrance: { ideal_pada, ideal_dir, box } | null,
 *       box_matrix_hash: string|null  // CANONICAL sha256 (composer parity)
 *     }
 *   }
 *
 * The snapshot is filled from the SAME resolved payload the screen renders
 * (`resolveAstroVastuForProject`). To guarantee "same as rendered", the display
 * helpers the screen uses (zoneName / buildBoxScaffold / idealZoneOf /
 * altZonesOf / allocationRoomLabel / allocationOccupants) live HERE and the
 * screen imports them — one implementation, zero drift.
 *
 * PURE/IMPURE split (repo convention, projectStore-style): the builder + patch
 * body are pure and unit-tested; `writeAstroVastuConfirmation` takes an
 * injectable io seam so the store-level test asserts the exact written shape
 * without Firebase.
 */
import { doc, onSnapshot, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from './firebase';
import type {
  AstroAllocationRow,
  AstroMember,
  AstroVastuResolved,
} from './astroVastu';

/* ════════════════════ Wire types (the fixed contract) ════════════════════ */

/** Breadwinner headline snapshot. All-null only when no member resolved. */
export interface AstroConfirmBreadwinner {
  name: string | null;
  ascendant: string | null;
  ideal_zone: string | null;
}

export interface AstroConfirmMember {
  name: string;
  relationship: string | null;
  ascendant: string | null;
  ideal_zone: string | null;
  positive_alternatives: string[];
  /** The marked bedroom zone this member matched to; null = not matched. */
  matched_bedroom: string | null;
}

export interface AstroConfirmAllocationRow {
  /** Rendered room identity, e.g. "South-West bedroom · Ground Floor". */
  room: string | null;
  /** Rendered occupant line, e.g. "Rajesh & Priya" (couples share one row). */
  member: string;
  source: 'engine' | 'override';
}

/**
 * The breadwinner's chart-IDEAL entrance (source:'chart-ideal' — not yet
 * reconciled against the home's marked entrance). `ideal_dir` carries the
 * engine's `recommendationZone` (the 16-zone direction label).
 */
export interface AstroConfirmEntrance {
  ideal_pada: string | null;
  ideal_dir: string | null;
  box: number | null;
}

export interface AstroConfirmSnapshot {
  breadwinner: AstroConfirmBreadwinner;
  members: AstroConfirmMember[];
  allocation: AstroConfirmAllocationRow[];
  entrance: AstroConfirmEntrance | null;
  /**
   * CANONICAL sha256-hex hash of the per-member Comparative Box Matrices —
   * Forge's composer recomputes it live at render time for drift detection
   * (`astro_confirmation_stale`). Null when no member carries a matrix
   * (drift is then uncheckable, never asserted). See `boxMatrixHash`.
   */
  box_matrix_hash: string | null;
}

export interface AstroVastuConfirmation {
  /** Staff Firebase Auth uid. */
  confirmed_by: string;
  confirmed_by_name: string;
  /** ISO timestamp of the confirm click. */
  confirmed_at: string;
  snapshot: AstroConfirmSnapshot;
}

/* ════════════════════ Shared display helpers (screen + snapshot) ═════════ */

/** Descriptive compass-zone copy (UI labels only — not astrology logic). */
export const ZONE_NAMES: Record<string, string> = {
  N: 'North · Uttara',
  NE: 'North-East · Ishanya',
  E: 'East · Purva',
  SE: 'South-East · Agneya',
  S: 'South · Dakshina',
  SW: 'South-West · Nairutya',
  W: 'West · Paschima',
  NW: 'North-West · Vayavya',
  C: 'Centre · Brahmasthan',
};

export function zoneName(zone: string): string {
  return ZONE_NAMES[zone?.toUpperCase?.()] ?? zone;
}

/** The member's primary ideal zone (klass 'ideal', else the first listed). */
export function idealZoneOf(m: AstroMember): string | null {
  return m.idealZones.find((z) => z.klass === 'ideal')?.zone ?? m.idealZones[0]?.zone ?? null;
}

/** The member's positive-alternative zones. */
export function altZonesOf(m: AstroMember): string[] {
  return m.idealZones.filter((z) => z.klass === 'positive').map((z) => z.zone);
}

/**
 * The marked bedroom zone a member is matched to — the zone core of the
 * screen's `memberMatch` verdict (the reconciled achievable zone is the engine
 * authority; the ideal-zone-is-marked fallback mirrors the render). Null =
 * chart pending or not matched.
 */
export function matchedBedroomOf(m: AstroMember, markedZones: string[]): string | null {
  if (m.chartStatus === 'pending') return null;
  if (m.achievableZone?.zone) return m.achievableZone.zone;
  const ideal = m.idealZones.find((z) => z.klass === 'ideal')?.zone;
  if (ideal && markedZones.some((z) => z.toUpperCase() === ideal.toUpperCase())) {
    return ideal;
  }
  return null;
}

/** The rendered room title of an allocation row ("South-West bedroom"). */
export function allocationRoomLabel(row: AstroAllocationRow): string {
  return row.zone ? `${zoneName(row.zone).split(' · ')[0]} bedroom` : 'Bedroom';
}

/**
 * The rendered occupant line of an allocation row. Forge returns a COMBINED
 * row for a couple — the full occupant set joins with " & ", falling back to
 * resolving personIds, then the scalar personId, before "Unassigned".
 */
export function allocationOccupants(
  row: AstroAllocationRow,
  nameById: Map<string, string>,
): string {
  if (row.members.length > 0) return row.members.join(' & ');
  if (row.personIds.length > 0) {
    return row.personIds.map((id) => nameById.get(id) ?? id).join(' & ');
  }
  return nameById.get(row.personId) || row.personId || 'Unassigned';
}

/**
 * Build the fixed 12-box scaffold for the Comparative Box Matrix. Rows are the
 * boxes (engine order — Box 1 = Ascendant), each with its life-context label.
 * Members agree on a box's life-context, so we take it from whichever member
 * supplies it; a box only appears once any member has resolved it. Sorted by
 * box number so the row order is stable regardless of who's still pending.
 */
export function buildBoxScaffold(
  members: AstroMember[],
): { box: number; lifeContext: string }[] {
  const map = new Map<number, string>();
  for (const m of members) {
    for (const row of m.boxMatrix) {
      const existing = map.get(row.box);
      if (existing === undefined || (existing === '' && row.lifeContext)) {
        map.set(row.box, row.lifeContext || existing || '');
      }
    }
  }
  return [...map.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([box, lifeContext]) => ({ box, lifeContext }));
}

/* ════════════════════ Box-matrix hash (CANONICAL — composer parity) ══════ */

/** sha256 hex via WebCrypto (browser secure contexts + Node ≥19 both have it). */
async function sha256Hex(s: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(s));
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * CANONICAL box-matrix hash — replicates Forge's composer algorithm VERBATIM
 * (functions repo `src/composer/lib/astroConfirmation.js`
 * `computeBoxMatrixHash`, commit e862358). Any deviation makes every confirmed
 * project read `astro_confirmation_stale: true` in report metadata:
 *   1. keep members with a non-empty `boxMatrix`;
 *   2. map to `{ name: String(name||''), matrix: boxMatrix.map(r =>
 *      ({ box: r.box ?? null, sign: r.sign || '' })) }` (row order preserved);
 *   3. sort by `name` (plain UTF-16 `<`/`>` — NOT localeCompare);
 *   4. sha256 hex of JSON.stringify(that array).
 * No members with a matrix → null (drift is then uncheckable, never asserted).
 * Cross-checked against the composer via the shared known-vector test in
 * test/astroVastuConfirm.test.cjs (byte-for-byte mirror of Forge's
 * `matrixMembers()` fixture).
 */
export async function boxMatrixHash(members: AstroMember[]): Promise<string | null> {
  const src = (Array.isArray(members) ? members : [])
    .filter((m) => m && Array.isArray(m.boxMatrix) && m.boxMatrix.length)
    .map((m) => ({
      name: String(m.name || ''),
      matrix: m.boxMatrix.map((r) => ({
        box: r.box != null ? r.box : null,
        sign: r.sign || '',
      })),
    }))
    .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
  if (!src.length) return null;
  return sha256Hex(JSON.stringify(src));
}

/* ════════════════════ Builder (PURE) ════════════════════ */

export interface ConfirmActor {
  /** Staff Firebase Auth uid. */
  uid: string;
  name: string;
}

/**
 * Build the confirmation from the SAME resolved payload the screen currently
 * renders. Total: an empty roster yields the kept-shape all-null breadwinner;
 * a pending chart yields null ascendant/ideal_zone; a missing entrance verdict
 * yields `entrance: null` (Forge mirrors these nulls). Async only for the
 * WebCrypto sha256 of the canonical box-matrix hash — everything else is pure.
 *
 * Composer integration invariants (Forge, e862358):
 *  - the breadwinner is ALWAYS also a row IN `members[]` (with their
 *    `matched_bedroom` filled) — his parser prepends a lossy minimal row
 *    otherwise;
 *  - `allocation[].member` strings are the verbatim rendered occupant lines
 *    (a couple stays exactly "A & B") — §F joins zones back to
 *    `matched_bedroom` by that string.
 */
export async function buildAstroVastuConfirmation(
  resolved: AstroVastuResolved,
  actor: ConfirmActor,
  confirmedAtIso: string = new Date().toISOString(),
): Promise<AstroVastuConfirmation> {
  const { members, allocation, markedBedroomZones } = resolved;
  // Same selection the screen headline uses.
  const bw = members.find((m) => m.isBreadwinner) ?? members[0] ?? null;
  const nameById = new Map(members.map((m) => [m.personId, m.name]));
  const bwVerdict = bw?.entranceVerdict ?? null;

  return {
    confirmed_by: actor.uid,
    confirmed_by_name: actor.name,
    confirmed_at: confirmedAtIso,
    snapshot: {
      breadwinner: {
        name: bw?.name ?? null,
        ascendant: bw?.ascendantSign ?? null,
        ideal_zone: bw ? idealZoneOf(bw) : null,
      },
      members: members.map((m) => ({
        name: m.name,
        relationship: m.relationToClient || null,
        ascendant: m.ascendantSign,
        ideal_zone: idealZoneOf(m),
        positive_alternatives: altZonesOf(m),
        matched_bedroom: matchedBedroomOf(m, markedBedroomZones),
      })),
      allocation: allocation.map((row) => ({
        room: [allocationRoomLabel(row), row.room].filter(Boolean).join(' · ') || null,
        member: allocationOccupants(row, nameById),
        source: row.source,
      })),
      // The chart-IDEAL entrance (not reconciled vs the marked entrance). A
      // 'no-ideal' disposition keeps the object with null pada/dir/box — the
      // same honest "no ideal entrance on this chart" the screen renders.
      entrance: bwVerdict
        ? {
            ideal_pada: bwVerdict.recommendationPada ?? null,
            ideal_dir: bwVerdict.recommendationZone ?? null,
            box: bwVerdict.recommendationBox ?? null,
          }
        : null,
      box_matrix_hash: await boxMatrixHash(members),
    },
  };
}

/* ════════════════════ Write path (pure body + injectable io) ═════════════ */

/**
 * PURE — the exact doc body the confirm write sends (projectStore-style:
 * bounded field set + audit stamp; `updatedAt` serverTimestamp is added by the
 * impure io, keeping this unit-testable).
 */
export function confirmationPatchBody(
  confirmation: AstroVastuConfirmation,
  actorName: string,
): Record<string, unknown> {
  return {
    astro_vastu_confirmation: confirmation,
    updatedBy: actorName,
  };
}

/** Injectable io seam — the store-level test asserts the written shape. */
export interface AstroConfirmWriteIo {
  updateProjectDoc: (projectId: string, body: Record<string, unknown>) => Promise<void>;
}

const realIo: AstroConfirmWriteIo = {
  updateProjectDoc: async (projectId, body) => {
    await updateDoc(doc(db, 'projects', projectId), {
      ...body,
      updatedAt: serverTimestamp(),
    });
  },
};

/**
 * Persist (or overwrite — Re-confirm) the confirmation on `projects/{pid}`.
 * REJECTS on failure so the screen can toast the real error message and stay
 * retryable — no optimistic commit, no fake success.
 */
export async function writeAstroVastuConfirmation(
  projectId: string,
  confirmation: AstroVastuConfirmation,
  actorName: string,
  io: AstroConfirmWriteIo = realIo,
): Promise<void> {
  await io.updateProjectDoc(projectId, confirmationPatchBody(confirmation, actorName));
}

/* ════════════════════ Read path (defensive normalise + live watch) ═══════ */

function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}
function str(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}
function strOrNull(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}
function numOrNull(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}
function arr(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}

function normMember(v: unknown): AstroConfirmMember | null {
  if (!isObj(v)) return null;
  const name = str(v.name);
  if (!name) return null;
  return {
    name,
    relationship: strOrNull(v.relationship),
    ascendant: strOrNull(v.ascendant),
    ideal_zone: strOrNull(v.ideal_zone),
    positive_alternatives: arr(v.positive_alternatives)
      .map((x) => str(x))
      .filter((s) => s.length > 0),
    matched_bedroom: strOrNull(v.matched_bedroom),
  };
}

function normAllocation(v: unknown): AstroConfirmAllocationRow | null {
  if (!isObj(v)) return null;
  const member = str(v.member);
  const room = strOrNull(v.room);
  if (!member && !room) return null;
  return {
    room,
    member,
    source: v.source === 'override' ? 'override' : 'engine',
  };
}

/**
 * Coerce whatever sits at `astro_vastu_confirmation` into a typed confirmation,
 * or null when the field is absent/unusable (pre-field docs, partial writes).
 * A garbage value must read as "not confirmed", never crash or fake a tick.
 */
export function normalizeConfirmation(v: unknown): AstroVastuConfirmation | null {
  if (!isObj(v)) return null;
  const confirmedBy = str(v.confirmed_by);
  const confirmedByName = str(v.confirmed_by_name);
  const confirmedAt = str(v.confirmed_at);
  // Minimum viable confirmation: someone identifiable confirmed at some time.
  if ((!confirmedBy && !confirmedByName) || !confirmedAt) return null;
  const s = isObj(v.snapshot) ? v.snapshot : {};
  const bwRaw = isObj(s.breadwinner) ? s.breadwinner : {};
  const entRaw = isObj(s.entrance) ? s.entrance : null;
  return {
    confirmed_by: confirmedBy,
    confirmed_by_name: confirmedByName || confirmedBy,
    confirmed_at: confirmedAt,
    snapshot: {
      breadwinner: {
        name: strOrNull(bwRaw.name),
        ascendant: strOrNull(bwRaw.ascendant),
        ideal_zone: strOrNull(bwRaw.ideal_zone),
      },
      members: arr(s.members)
        .map(normMember)
        .filter((m): m is AstroConfirmMember => m !== null),
      allocation: arr(s.allocation)
        .map(normAllocation)
        .filter((a): a is AstroConfirmAllocationRow => a !== null),
      entrance: entRaw
        ? {
            ideal_pada: strOrNull(entRaw.ideal_pada),
            ideal_dir: strOrNull(entRaw.ideal_dir),
            box: numOrNull(entRaw.box),
          }
        : null,
      box_matrix_hash: strOrNull(s.box_matrix_hash),
    },
  };
}

/**
 * PURE — does the project carry ANY astro subject (owner bug #5, 2026-07-03)?
 * The subject is an AVBP Group (`linked_group_id`) OR AVBP Contacts
 * (`linked_contact_ids`) — or, on legacy docs, the astro-store links
 * (`astro_family_id` / `astro_person_ids`). A stored
 * `astro_vastu_confirmation` is only MEANINGFUL while a subject exists: on
 * VP678 the owner cleared every link, yet the stage chip stayed green and the
 * "Confirmed by" banner survived, because the confirmation field outlives the
 * links. Write-side now voids it on subject-removal
 * (projectLinkedGroupBody/projectLinkedContactsBody); this read-side gate is
 * the defense-in-depth for dangling docs that predate that rule — no subject ⇒
 * treat as not-confirmed (stage not done, no banner). Defensive across
 * partial/pre-field docs; never throws.
 */
export function hasAstroSubject(
  p:
    | {
        linked_group_id?: string | null;
        linked_contact_ids?: string[] | null;
        astro_family_id?: string | null;
        astro_person_ids?: string[] | null;
      }
    | null
    | undefined,
): boolean {
  if (!p || typeof p !== 'object') return false;
  const hasIds = (v: unknown): boolean =>
    Array.isArray(v) && v.some((x) => typeof x === 'string' && x.length > 0);
  return !!(
    (typeof p.linked_group_id === 'string' && p.linked_group_id) ||
    hasIds(p.linked_contact_ids) ||
    (typeof p.astro_family_id === 'string' && p.astro_family_id) ||
    hasIds(p.astro_person_ids)
  );
}

/**
 * Live-watch the confirmation on `projects/{pid}` — drives the confirmed
 * banner AND the Astro-Vastu stage tick, and reconciles a Confirm/Re-confirm
 * write back into the UI without a refetch. On a stream error the callback
 * gets null: a broken stream must never fake a tick (staff can re-confirm;
 * the UI must never show a confirmation it cannot read).
 */
export function watchAstroVastuConfirmation(
  projectId: string,
  cb: (c: AstroVastuConfirmation | null) => void,
): () => void {
  return onSnapshot(
    doc(db, 'projects', projectId),
    (snap) => cb(normalizeConfirmation(snap.data()?.astro_vastu_confirmation)),
    () => cb(null),
  );
}
